import { useMutation, useQuery } from '@apollo/client';
import {
  Alert,
  Button,
  Card,
  Col,
  Form,
  InputNumber,
  message,
  Row,
  Select,
  Space,
  Spin,
  Typography,
} from 'antd';
import { GET_APP_SETTINGS_QUERY } from 'modules/settings/graphql/queries';
import { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { SAVE_NIGHTLY_COUNT_MUTATION } from '../../graphql/mutations';
import {
  GET_NIGHTLY_COUNT_FOR_TODAY_QUERY,
  GET_NIGHTLY_COUNTS_LIST_QUERY,
} from '../../graphql/queries';
import { useNightlyCountAccess } from '../../hooks/useNightlyCountAccess';
import {
  INightlyCountSettingsJson,
  INightlyCountSettingsProduct,
  NIGHTLY_COUNTS_SETTINGS_TAB,
} from '../../types/settings';

const { Title, Text } = Typography;

interface IExistingItem {
  productKey: string;
  count: number;
}

interface IExistingCount {
  id: string;
  officeId: string;
  countDate: string;
  items: { productKey: string; count: number }[];
}

const productKeyFor = (p: INightlyCountSettingsProduct): string =>
  String(p.woocommerceProductId);

const EnterNightlyCountForm: FC = () => {
  const { t } = useTranslation('nightlyCount.EnterNightlyCountForm');
  const access = useNightlyCountAccess();
  const [selectedOfficeId, setSelectedOfficeId] = useState<string | undefined>(
    undefined
  );
  const [counts, setCounts] = useState<Record<string, number>>({});

  useEffect(() => {
    if (!selectedOfficeId && access.allowedOffices.length > 0) {
      setSelectedOfficeId(access.allowedOffices[0].id);
    }
  }, [access.allowedOffices, selectedOfficeId]);

  const settingsQuery = useQuery<{
    getAppSettings: { settingsJson: INightlyCountSettingsJson } | null;
  }>(GET_APP_SETTINGS_QUERY, {
    variables: { input: { settingsTab: NIGHTLY_COUNTS_SETTINGS_TAB } },
    fetchPolicy: 'cache-and-network',
  });

  const settings: INightlyCountSettingsJson =
    settingsQuery.data?.getAppSettings?.settingsJson || {};

  const officeProducts: INightlyCountSettingsProduct[] = useMemo(
    () => settings.products || [],
    [settings.products]
  );

  const todayQuery = useQuery<{
    getNightlyCountForToday: IExistingCount | null;
  }>(GET_NIGHTLY_COUNT_FOR_TODAY_QUERY, {
    variables: { input: { officeId: selectedOfficeId } },
    skip: !selectedOfficeId,
    fetchPolicy: 'cache-and-network',
  });

  const existingCount = todayQuery.data?.getNightlyCountForToday;
  const isEdit = !!existingCount;

  useEffect(() => {
    if (existingCount?.items) {
      const next: Record<string, number> = {};
      existingCount.items.forEach((it: IExistingItem) => {
        next[it.productKey] = it.count;
      });
      setCounts(next);
    } else {
      setCounts({});
    }
  }, [existingCount?.id]);

  const [saveNightlyCount, { loading: isSaving }] = useMutation(
    SAVE_NIGHTLY_COUNT_MUTATION,
    {
      refetchQueries: [
        { query: GET_NIGHTLY_COUNTS_LIST_QUERY, variables: { input: {} } },
        {
          query: GET_NIGHTLY_COUNT_FOR_TODAY_QUERY,
          variables: { input: { officeId: selectedOfficeId } },
        },
      ],
      awaitRefetchQueries: true,
    }
  );

  const handleSubmit = async () => {
    if (!selectedOfficeId) return;
    if (officeProducts.length === 0) return;
    try {
      const items = officeProducts.map((p) => {
        const key = productKeyFor(p);
        return {
          productKey: key,
          productTitle: p.productTitle,
          woocommerceProductId: Number(p.woocommerceProductId),
          productClass: p.productClass,
          productBloodType: p.productBloodType || undefined,
          count: Number(counts[key] ?? 0),
        };
      });
      await saveNightlyCount({
        variables: {
          input: {
            officeId: selectedOfficeId,
            items,
          },
        },
      });
      message.success(t('saveSuccess'));
    } catch (e) {
      message.error(`${t('saveError')}: ${(e as Error).message}`);
    }
  };

  if (access.isLoadingAccess || settingsQuery.loading) {
    return (
      <Card>
        <Spin />
      </Card>
    );
  }

  if (!access.canEnterForAny) {
    return (
      <Card>
        <Alert type="warning" message={t('notAllowed')} />
      </Card>
    );
  }

  const officeSelectDisabled =
    !access.isAdminOrOps && access.allowedOffices.length === 1;
  const officeOptions = access.isAdminOrOps
    ? access.allowedOffices.map((o) => ({
        value: o.id,
        label: `${o.name} (${o.regionName})`,
      }))
    : access.allowedOffices.map((o) => ({
        value: o.id,
        label: `${o.name} (${o.regionName})`,
      }));

  return (
    <Card>
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Title level={4}>{t('enterCountForOffice')}</Title>

        <Row gutter={16}>
          <Col xs={24} sm={12} md={8}>
            <Form layout="vertical">
              <Form.Item label={t('office')}>
                <Select
                  value={selectedOfficeId}
                  onChange={(v) => setSelectedOfficeId(v)}
                  options={officeOptions}
                  disabled={officeSelectDisabled}
                  placeholder={t('selectOffice')}
                  showSearch
                  optionFilterProp="label"
                />
              </Form.Item>
            </Form>
          </Col>
        </Row>

        {isEdit && <Alert type="info" message={t('todaysCountExists')} />}

        {selectedOfficeId && officeProducts.length === 0 && (
          <Alert type="warning" message={t('noProductsConfigured')} />
        )}

        {officeProducts.length > 0 && (
          <Space direction="vertical" size="small" style={{ width: '100%' }}>
            {officeProducts.map((p) => {
              const key = productKeyFor(p);
              const typeSuffix = p.productBloodType
                ? ` (${p.productBloodType})`
                : '';
              return (
                <Row key={p.id} gutter={16} align="middle">
                  <Col xs={16}>
                    <Text>
                      {p.productTitle}
                      {typeSuffix}{' '}
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        [{p.productClass}]
                      </Text>
                    </Text>
                  </Col>
                  <Col xs={8}>
                    <InputNumber
                      min={0}
                      value={counts[key] ?? 0}
                      onChange={(v) =>
                        setCounts((c) => ({ ...c, [key]: Number(v ?? 0) }))
                      }
                      style={{ width: '100%' }}
                    />
                  </Col>
                </Row>
              );
            })}

            <Row justify="end">
              <Col>
                <Button
                  type="primary"
                  onClick={handleSubmit}
                  loading={isSaving}
                >
                  {isEdit ? t('saveEdit') : t('submitCount')}
                </Button>
              </Col>
            </Row>
          </Space>
        )}
      </Space>
    </Card>
  );
};

export default EnterNightlyCountForm;
